import React, { useState } from 'react';
import { useGame } from '../context/GameContext';
import HUD from '../components/HUD';
import IntentionModal from '../components/IntentionModal';

const COLORS = ['#00ff88', '#00e5ff', '#ffd700', '#ff6b35', '#ff4081', '#b388ff', '#ff3333', '#00ffff'];

export default function Home() {
  const {
    intentions, categories, completeIntention, moveIntention, deleteIntention,
    addCategory, renameCategory, deleteCategory
  } = useGame();

  const [showModal, setShowModal] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverCat, setDragOverCat] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('#00ff88');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);

  const handleDragStart = (e, intention) => {
    setDragItem(intention);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, cat) => {
    e.preventDefault();
    setDragOverCat(cat);
  };

  const handleDrop = async (e, cat) => {
    e.preventDefault();
    if (dragItem && dragItem.category !== cat) {
      await moveIntention(dragItem.id, cat);
    }
    setDragItem(null);
    setDragOverCat(null);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverCat(null);
  };

  const handleRename = async (id) => {
    if (editingName.trim()) await renameCategory(id, editingName.trim());
    setEditingCat(null);
    setEditingName('');
  };

  const handleDeleteCat = async (id) => {
    if (confirmDeleteCat === id) {
      await deleteCategory(id);
      setConfirmDeleteCat(null);
    } else {
      setConfirmDeleteCat(id);
      setTimeout(() => setConfirmDeleteCat(null), 3000);
    }
  };

  const handleAddCat = async () => {
    if (!newCatName.trim()) return;
    await addCategory(newCatName.trim(), newCatColor);
    setNewCatName('');
    setNewCatColor('#00ff88');
    setShowAddCat(false);
  };

  const totalCompleted = intentions.filter(i => i.completed).length;

  return (
    <div className="page home-page">
      <HUD />
      <div className="home-header">
        <h1 className="page-title">DAILY INTENTIONS</h1>
        <div className="home-header-actions">
          {totalCompleted > 0 && (
            <button className="btn-sm btn-toggle-completed" onClick={() => setShowCompleted(p => !p)}>
              {showCompleted ? 'HIDE COMPLETED' : `SHOW COMPLETED (${totalCompleted})`}
            </button>
          )}
          <button className="btn-sm" onClick={() => setShowAddCat(p => !p)}>
            + ADD CATEGORY
          </button>
          <button className="btn-primary btn-create" onClick={() => setShowModal(true)}>
            + CREATE INTENTION
          </button>
        </div>
      </div>

      {showAddCat && (
        <div className="add-cat-form">
          <input
            className="tool-input"
            placeholder="Category name..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCat()}
          />
          <div className="color-picker">
            {COLORS.map(c => (
              <button
                key={c}
                className={`color-swatch ${newCatColor === c ? 'selected' : ''}`}
                style={{ background: c }}
                onClick={() => setNewCatColor(c)}
              />
            ))}
          </div>
          <button className="btn-primary" onClick={handleAddCat}>ADD</button>
          <button className="btn-sm btn-cancel" onClick={() => setShowAddCat(false)}>CANCEL</button>
        </div>
      )}

      <div className="categories-grid">
        {categories.map(cat => {
          const allCatIntentions = intentions.filter(i => i.category === cat.name);
          const activeIntentions = allCatIntentions.filter(i => !i.completed);
          const completedIntentions = allCatIntentions.filter(i => i.completed);
          const visibleIntentions = showCompleted ? allCatIntentions : activeIntentions;
          const isOver = dragOverCat === cat.name;

          return (
            <div
              key={cat.id}
              className={`category-column ${isOver ? 'drag-over' : ''}`}
              style={{ '--cat-color': cat.color }}
              onDragOver={e => handleDragOver(e, cat.name)}
              onDrop={e => handleDrop(e, cat.name)}
            >
              <div className="cat-header">
                {editingCat === cat.id ? (
                  <input
                    className="cat-rename-input"
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(cat.id); if (e.key === 'Escape') setEditingCat(null); }}
                    autoFocus
                  />
                ) : (
                  <span className="cat-title" style={{ color: cat.color }}>{cat.name.toUpperCase()}</span>
                )}
                <span className="cat-count">{completedIntentions.length}/{allCatIntentions.length}</span>
                <button
                  className="cat-edit-btn"
                  onClick={() => { setEditingCat(cat.id); setEditingName(cat.name); }}
                  title="Rename"
                >
                  ?
                </button>
                {editingCat === cat.id && (
                  <button className="cat-edit-btn" onClick={() => handleRename(cat.id)}>?</button>
                )}
                <button
                  className={`cat-delete-btn ${confirmDeleteCat === cat.id ? 'confirm' : ''}`}
                  onClick={() => handleDeleteCat(cat.id)}
                  title={confirmDeleteCat === cat.id ? 'Click again to confirm delete' : 'Delete category'}
                >
                  {confirmDeleteCat === cat.id ? '!' : 'X'}
                </button>
              </div>
              <div className="intention-list">
                {visibleIntentions.length === 0 && (
                  <div className="empty-slot">
                    {completedIntentions.length > 0 && !showCompleted
                      ? `${completedIntentions.length} done - all clear!`
                      : 'Drop intentions here'}
                  </div>
                )}
                {visibleIntentions.map(intention => (
                  <div
                    key={intention.id}
                    className={`intention-card ${intention.completed ? 'completed' : ''} ${dragItem?.id === intention.id ? 'dragging' : ''}`}
                    draggable={!intention.completed}
                    onDragStart={e => handleDragStart(e, intention)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="intention-text">{intention.text}</div>
                    {intention.xpMultiplier > 1 && (
                      <div className="xp-bonus">x{intention.xpMultiplier} XP</div>
                    )}
                    <div className="intention-actions">
                      {!intention.completed && (
                        <button className="btn-complete" onClick={() => completeIntention(intention.id)}>
                          DONE
                        </button>
                      )}
                      {intention.completed && <span className="completed-badge">DONE</span>}
                      <button className="btn-delete" onClick={() => deleteIntention(intention.id)}>X</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showModal && <IntentionModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
